import { API } from '../config';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, Link2, Unlink, RefreshCw, Copy, MessageCircle, Zap, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';


export default function TelegramLinkPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [linkStatus, setLinkStatus] = useState(null);
  const [linkCode, setLinkCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/telegram/link/status`, { headers: getAuthHeaders() });
      setLinkStatus(res.data);
    } catch (err) {
      toast.error('Durum alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateCode = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/telegram/link/generate-code`, {}, { headers: getAuthHeaders() });
      setLinkCode(res.data);
      toast.success('Bağlantı kodu oluşturuldu');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kod oluşturulamadı');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMockLink = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/telegram/link/mock-link`, {}, { headers: getAuthHeaders() });
      toast.success('Mock Telegram bağlantısı oluşturuldu');
      setLinkCode(null);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bağlantı oluşturulamadı');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlink = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/telegram/link/unlink`, {}, { headers: getAuthHeaders() });
      toast.success('Telegram bağlantısı kaldırıldı');
      setLinkCode(null);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bağlantı kaldırılamadı');
    } finally {
      setActionLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Kod kopyalandı');
  };

  const getStatusColor = (status) => {
    const colors = {
      linked: 'bg-green-500',
      pending: 'bg-yellow-500',
      disabled: 'bg-muted-foreground',
      not_linked: 'bg-muted-foreground',
      error: 'bg-destructive',
      mock: 'bg-yellow-500'
    };
    return colors[status] || 'bg-muted-foreground';
  };

  const getStatusBadgeVariant = (status) => {
    if (status === 'linked') return 'default';
    if (status === 'pending') return 'secondary';
    if (status === 'disabled' || status === 'error') return 'destructive';
    return 'outline';
  };

  return (
    <div className="min-h-dvh bg-background">
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border/70 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate('/app')}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <MessageCircle className="w-5 h-5 text-primary" />
        <h1 className="text-lg font-semibold font-display">Telegram Bağlantısı</h1>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Yükleniyor...</div>
        ) : (
          <>
            {/* Status Card */}
            <div className="bg-card rounded-xl border border-border/70 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-medium">Bağlantı Durumu</h2>
                <Button variant="ghost" size="sm" className="rounded-xl text-xs" onClick={fetchStatus} disabled={actionLoading}>
                  <RefreshCw className="w-3 h-3 mr-1" /> Yenile
                </Button>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(linkStatus?.status)}`}></div>
                <div>
                  <Badge variant={getStatusBadgeVariant(linkStatus?.status)} className="text-xs">
                    {linkStatus?.status_label || 'Bilinmiyor'}
                  </Badge>
                </div>
              </div>

              {/* Adapter Mode Info */}
              <div className="bg-secondary/30 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium">Adaptör Modu</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {linkStatus?.adapter_mode === 'real' ? 'Gerçek mod - Telegram Bot API aktif' :
                   linkStatus?.adapter_mode === 'mock' ? 'Mock mod - Simülasyon modunda çalışıyor' :
                   'Bilinmiyor'}
                </p>
              </div>

              {/* Linked Info */}
              {linkStatus?.linked && (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Kullanıcı adı:</span>
                    <span className="font-medium">@{linkStatus.telegram_username || 'Bilinmiyor'}</span>
                  </div>
                  {linkStatus.telegram_display_name && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Görünen ad:</span>
                      <span className="font-medium">{linkStatus.telegram_display_name}</span>
                    </div>
                  )}
                  {linkStatus.telegram_chat_id && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Sohbet ID:</span>
                      <span className="font-mono text-xs">{linkStatus.telegram_chat_id}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2">
                {!linkStatus?.linked && linkStatus?.status !== 'pending' && (
                  <>
                    {linkStatus?.adapter_mode === 'real' ? (
                      <Button onClick={handleGenerateCode} disabled={actionLoading} className="w-full rounded-xl">
                        <Link2 className="w-4 h-4 mr-2" />
                        {actionLoading ? 'Hazırlanıyor...' : 'Telegram Bağla'}
                      </Button>
                    ) : (
                      <Button onClick={handleMockLink} disabled={actionLoading} className="w-full rounded-xl">
                        <Link2 className="w-4 h-4 mr-2" />
                        {actionLoading ? 'Bağlanıyor...' : 'Mock Telegram Bağla'}
                      </Button>
                    )}
                    {linkStatus?.adapter_mode === 'mock' && (
                      <p className="text-[10px] text-muted-foreground text-center">
                        Mock modunda test bağlantısı oluşturulur. Gerçek bağlantı için bot tokenı gereklidir.
                      </p>
                    )}
                  </>
                )}

                {linkStatus?.status === 'pending' && (
                  <div className="text-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Bağlantı kodu oluşturuldu. Bot'a kodu gönderin.</p>
                    <Button variant="outline" onClick={handleGenerateCode} disabled={actionLoading} className="mt-2 rounded-xl text-xs">
                      Yeni Kod Oluştur
                    </Button>
                  </div>
                )}

                {linkStatus?.linked && (
                  <Button variant="destructive" onClick={handleUnlink} disabled={actionLoading} className="w-full rounded-xl">
                    <Unlink className="w-4 h-4 mr-2" />
                    {actionLoading ? 'Kaldırılıyor...' : 'Bağlantıyı Kaldır'}
                  </Button>
                )}
              </div>
            </div>

            {/* Link Code Card */}
            {linkCode && (
              <div className="bg-card rounded-xl border border-primary/30 p-5">
                <h3 className="text-sm font-medium mb-3">Bağlantı Kodu</h3>
                <div className="flex items-center justify-center gap-3 mb-3">
                  <code className="text-3xl font-mono font-bold tracking-widest text-primary">
                    {linkCode.link_code}
                  </code>
                  <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => copyCode(linkCode.link_code)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">{linkCode.instructions}</p>
                {linkCode.bot_username && (
                  <p className="text-xs text-center mt-2">
                    Bot: <span className="font-medium text-primary">@{linkCode.bot_username}</span>
                  </p>
                )}
              </div>
            )}

            {/* Info Card */}
            <div className="bg-secondary/20 rounded-xl p-4">
              <h3 className="text-xs font-medium mb-2">Nasıl Çalışır?</h3>
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>"Telegram Bağla" butonuna tıklayın</li>
                <li>Oluşturulan kodu kopyalayın</li>
                <li>Telegram bot'una bu kodu gönderin</li>
                <li>Bağlantı otomatik olarak kurulur</li>
              </ol>
              <p className="text-[10px] text-muted-foreground mt-2">
                Bağlantıyı kaldırdığınızda geçmiş mesajlar korunur.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
